/* eslint-disable */
/**
 * Role Hierarchy Utility for Frontend
 * Mirrors the backend role hierarchy logic
 *
 * Hierarchy (from lowest to highest):
 * 0: member
 * 1: supervisor
 * 2: director
 * 3: band_administrator
 * 4: chief
 * 5: admin
 * 6: superadmin
 */

export type UserRole = "member" | "supervisor" | "director" | "band_administrator" | "chief" | "admin" | "superadmin" | "project manager";

const roleHierarchy: Record<UserRole, number> = {
  member: 0,
  supervisor: 1,
  director: 2,
  band_administrator: 3,
  chief: 4,
  admin: 5,
  superadmin: 6,
  "project manager": 5, // Same level as admin
};

export const canManageRole = (
  actorRole: UserRole | undefined,
  targetRole: UserRole | undefined,
): boolean => {
  if (!actorRole || !targetRole) {
    return false;
  }

  const actorHierarchy = roleHierarchy[actorRole];
  const targetHierarchy = roleHierarchy[targetRole];

  if (actorRole === "superadmin") {
    return true;
  }

  if (actorRole === "admin" || actorRole === "project manager") {
    return targetRole !== "superadmin";
  }

  return actorHierarchy >= targetHierarchy;
};

export const getManagedRoles = (role: UserRole | undefined): UserRole[] => {
  if (!role) {
    return [];
  }

  const hierarchy = roleHierarchy[role];

  // Superadmin and admin can manage all roles (except superadmin for admin)
  if (role === "superadmin") {
    return [
      "superadmin",
      "admin",
      "chief",
      "band_administrator",
      "director",
      "supervisor",
      "member",
      "project manager",
    ];
  }

  if (role === "admin" || role === "project manager") {
    return [
      "admin",
      "chief",
      "band_administrator",
      "director",
      "supervisor",
      "member",
      "project manager",
    ];
  }

  return (Object.entries(roleHierarchy) as [UserRole, number][])
    .filter(([_, hierarchyLevel]) => hierarchyLevel <= hierarchy)
    .map(([roleName]) => roleName);
};

export const getRoleHierarchyLevel = (role: UserRole | undefined): number => {
  if (!role) {
    return -1;
  }
  return roleHierarchy[role] ?? -1;
};

export const isRoleHigherThan = (
  actorRole: UserRole | undefined,
  targetRole: UserRole | undefined
): boolean => {
  if (!actorRole || !targetRole) {
    return false;
  }
  return roleHierarchy[actorRole] > roleHierarchy[targetRole];
};
